import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const ToastContext = createContext(null);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((toast) => {
    const id = toast.id || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const normalized = {
      id,
      type: toast.type || 'info', // info | success | warning | error
      message: String(toast.message || toast),
      duration: toast.duration || 3500
    };
    setToasts((prev) => [...prev, normalized]);
    // Auto-remove after duration (only if duration > 0)
    if (normalized.duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter(t => t.id !== id));
      }, normalized.duration);
    }
    return id; // Return the toast ID so it can be manually removed
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter(t => t.id !== id));
  }, []);

  // Soft-replace window.alert to route messages to toasts (non-blocking)
  useEffect(() => {
    const originalAlert = window.alert;
    window.alert = (msg) => addToast({ type: 'info', message: msg });
    return () => {
      window.alert = originalAlert;
    };
  }, [addToast]);

  // Listen for custom toast events from components outside the React tree (e.g., portals)
  useEffect(() => {
    const handleCustomToast = (event) => {
      const { message, type } = event.detail;
      addToast({ message, type: type || 'info' });
    };

    window.addEventListener('show-toast', handleCustomToast);
    return () => {
      window.removeEventListener('show-toast', handleCustomToast);
    };
  }, [addToast]);

  const value = useMemo(() => ({
    addToast,
    removeToast,
    toasts
  }), [addToast, removeToast, toasts]);

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
};


